import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AuthenticatedArea,
  AuthPortal,
  InviteAcceptancePortal,
  SuiteAccountMenuControls,
  useAccessToken,
  useSuiteAccountAvatar,
  useSuitePreferences,
  useTribunaleaAccess,
} from "@tribunalea/auth/react";
import {
  ACTIVITY_APPLICATION_IDS,
  ACTIVITY_TOOL_IDS,
  APPLICATION_IDS,
  canAccessApplication,
  createFullSuiteAccess,
  TRIBUNALEA_APPLICATIONS,
} from "@tribunalea/shared";
import {
  Activity,
  BadgeDollarSign,
  BarChart3,
  BookOpen,
  Building2,
  ChartNoAxesCombined,
  Coins,
  Gift,
  MailPlus,
  MessageCircle,
  PenTool,
  ShieldCheck,
} from "lucide-react";
import {
  CreditsSettingsPage,
  InstitutionalAccessPage,
  ProductShell,
  SignatureSettingsPage,
  useActivityTracking,
  useRouter,
} from "@tribunalea/ui";
import { createApi } from "./lib/api.js";
import { SupportImpersonationPage } from "./SupportImpersonationPage.jsx";
import { DoctrineAdminPage } from "./DoctrineAdminPage.jsx";
import { JobsAdminPage } from "./JobsAdminPage.jsx";
import { InvitationsAdminPage } from "./InvitationsAdminPage.jsx";
import { InstitutionPage } from "./InstitutionPage.jsx";
import { OrganizationSetupPage } from "./OrganizationSetupPage.jsx";
import { BillingPricingAdminPage } from "./BillingPricingAdminPage.jsx";
import { UserActivityAdminPage } from "./UserActivityAdminPage.jsx";
import { FinancialReportAdminPage } from "./FinancialReportAdminPage.jsx";
import { WhatsappSettingsPage } from "./WhatsappSettingsPage.jsx";
import { AdminCreditGrantPage } from "./AdminCreditGrantPage.jsx";

const ApiContext = createContext(null);
const useApi = () => useContext(ApiContext);

const portalUrl = import.meta.env.DEV
  ? "http://localhost:3000/portal"
  : "https://tribunalea.com/portal";
const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";

const products = [
  {
    applicationId: APPLICATION_IDS.PRAXIS,
    name: "Praxis",
    href: import.meta.env.VITE_PRAXIS_URL || (
      import.meta.env.DEV
        ? "http://localhost:5173"
        : "https://praxis.tribunalea.com"
    ),
  },
  {
    applicationId: APPLICATION_IDS.INSTRUMENTA,
    name: "Instrumenta",
    href: import.meta.env.VITE_INSTRUMENTA_URL || (
      import.meta.env.DEV
        ? "http://localhost:5174"
        : "https://instrumenta.tribunalea.com"
    ),
  },
  {
    applicationId: APPLICATION_IDS.LEXIA,
    name: "Lexia",
    href: import.meta.env.VITE_LEXIA_URL || (
      import.meta.env.DEV
        ? "http://localhost:5176"
        : "https://lexia.tribunalea.com"
    ),
  },
  {
    applicationId: APPLICATION_IDS.CIVITA,
    name: "Civita",
    href: import.meta.env.VITE_CIVITA_URL || (
      import.meta.env.DEV
        ? "http://localhost:5179"
        : "https://civita.tribunalea.com"
    ),
  },
  {
    name: "Cuenta",
    href: import.meta.env.VITE_ACCOUNT_URL || (
      import.meta.env.DEV
        ? "http://localhost:5175"
        : "https://account.tribunalea.com"
    ),
  },
];

function accountNavigation(
  supportEnabled,
  invitationsEnabled,
  creditAdminEnabled,
  billingAdminEnabled,
  activityAdminEnabled,
  organizationWorkspace,
) {
  const internalAdministrationItems = [
    ...(invitationsEnabled
      ? [{ to: "/invitaciones", label: "Invitaciones", icon: MailPlus }]
      : []),
    ...(creditAdminEnabled
      ? [{ to: "/otorgar-creditos", label: "Otorgar créditos", icon: Gift }]
      : []),
    ...(billingAdminEnabled
      ? [
          { to: "/precios", label: "Precios", icon: BadgeDollarSign },
          { to: "/reporte-financiero", label: "Reporte financiero", icon: ChartNoAxesCombined },
        ]
      : []),
    ...(activityAdminEnabled
      ? [{ to: "/actividad", label: "Actividad de usuarios", icon: BarChart3 }]
      : []),
    ...(supportEnabled
      ? [
          { to: "/soporte", label: "Acceso de soporte", icon: ShieldCheck },
          { to: "/trabajos", label: "Trabajos", icon: Activity },
          { to: "/doctrina", label: "Administrar doctrina", icon: BookOpen },
        ]
      : []),
  ];

  return [
    {
      label: "Configuración general",
      items: [
        { to: "/creditos", label: "Créditos", icon: Coins },
        { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle },
        { to: "/firma", label: "Firma para escritos", icon: PenTool },
      ],
    },
    ...(organizationWorkspace
      ? [{
          label: "Administración de la organización",
          items: [
            { to: "/organizacion", label: "Organización", icon: Building2 },
          ],
        }]
      : []),
    ...(internalAdministrationItems.length
      ? [{
          label: "Administración interna",
          items: internalAdministrationItems,
        }]
      : []),
  ];
}

export function accountToolForPath(path) {
  if (path === "/creditos") return ACTIVITY_TOOL_IDS.ACCOUNT_CREDITS;
  if (path === "/whatsapp") return null;
  if (path === "/firma") return ACTIVITY_TOOL_IDS.ACCOUNT_SIGNATURE;
  if (path === "/organizacion") return ACTIVITY_TOOL_IDS.ACCOUNT_ORGANIZATION;
  return null;
}

function AccountRoutes({
  access,
  accessError = null,
  accessLoading = false,
  accountAvatar,
  authEnabled,
  accountControls,
  getAccessToken,
  supportAccess,
  invitationAccess,
  creditAdminAccess,
  billingAdminAccess,
  activityAdminAccess,
  reloadAccess = null,
}) {
  const api = useApi();
  const { path, search, navigate } = useRouter();
  const supportEnabled = Boolean(supportAccess?.enabled);
  const invitationsEnabled = Boolean(invitationAccess?.enabled);
  const creditAdminEnabled = Boolean(creditAdminAccess?.enabled);
  const billingAdminEnabled = Boolean(billingAdminAccess?.enabled);
  const activityAdminEnabled = Boolean(activityAdminAccess?.enabled);
  const institutionalWorkspace = Boolean(access?.workspace?.institutional);
  const organizationWorkspace = access?.workspace?.type === "organization";
  const visibleProducts = products.filter((product) => (
    !product.applicationId
    || canAccessApplication(access, product.applicationId)
  ));
  const hiddenProductNames = TRIBUNALEA_APPLICATIONS
    .filter(({ id }) => !canAccessApplication(access, id))
    .map(({ name }) => name);
  useActivityTracking({
    apiUrl,
    getAccessToken,
    appId: ACTIVITY_APPLICATION_IDS.ACCOUNT,
    toolId: accountToolForPath(path),
    portalEntry: new URLSearchParams(search).get("entry") === "portal",
    enabled: authEnabled && !accessLoading && !accessError,
    scopeKey: access?.workspace?.id || "personal",
  });

  useEffect(() => {
    if (
      supportAccess === null
      || invitationAccess === null
      || creditAdminAccess === null
      || billingAdminAccess === null
      || activityAdminAccess === null
    ) return;
    const allowedPaths = [
      "/creditos",
      "/whatsapp",
      "/firma",
      ...(organizationWorkspace ? ["/organizacion"] : []),
      ...(invitationsEnabled ? ["/invitaciones"] : []),
      ...(creditAdminEnabled ? ["/otorgar-creditos"] : []),
      ...(billingAdminEnabled ? ["/precios"] : []),
      ...(billingAdminEnabled ? ["/reporte-financiero"] : []),
      ...(activityAdminEnabled ? ["/actividad"] : []),
      ...(supportEnabled ? ["/soporte", "/trabajos", "/doctrina"] : []),
    ];
    if (!allowedPaths.includes(path)) navigate("/creditos", { replace: true });
  }, [
    invitationAccess,
    invitationsEnabled,
    creditAdminAccess,
    creditAdminEnabled,
    billingAdminAccess,
    billingAdminEnabled,
    activityAdminAccess,
    activityAdminEnabled,
    organizationWorkspace,
    navigate,
    path,
    supportAccess,
    supportEnabled,
  ]);

  return (
    <ProductShell
      productName="Cuenta"
      products={visibleProducts}
      hiddenProductNames={hiddenProductNames}
      navigation={accountNavigation(
        supportEnabled,
        invitationsEnabled,
        creditAdminEnabled,
        billingAdminEnabled,
        activityAdminEnabled,
        organizationWorkspace,
      )}
      portalUrl={portalUrl}
      accountAvatar={accountAvatar}
      workspace={access?.workspace}
      accountControls={accountControls}
      accountSettingsHref="/creditos"
      demo={!authEnabled}
    >
      {accessLoading || accessError ? (
        <InstitutionalAccessPage
          error={accessError}
          loading={accessLoading}
          onRetry={reloadAccess}
        />
      ) : (path === "/invitaciones"
          || path === "/otorgar-creditos"
          || path === "/precios"
          || path === "/reporte-financiero")
        && (invitationAccess === null
          || creditAdminAccess === null
          || billingAdminAccess === null) ? (
        <div className="auth-loading" role="status">Verificando acceso…</div>
      ) : path === "/actividad" && activityAdminAccess === null ? (
        <div className="auth-loading" role="status">Verificando acceso…</div>
      ) : ["/soporte", "/trabajos", "/doctrina"].includes(path) && supportAccess === null ? (
        <div className="auth-loading" role="status">Verificando acceso…</div>
      ) : path === "/invitaciones" && invitationsEnabled ? (
        <InvitationsAdminPage api={api} />
      ) : path === "/otorgar-creditos" && creditAdminEnabled ? (
        <AdminCreditGrantPage api={api} access={creditAdminAccess} />
      ) : path === "/precios" && billingAdminEnabled ? (
        <BillingPricingAdminPage api={api} />
      ) : path === "/reporte-financiero" && billingAdminEnabled ? (
        <FinancialReportAdminPage api={api} />
      ) : path === "/actividad" && activityAdminEnabled ? (
        <UserActivityAdminPage api={api} />
      ) : path === "/organizacion" && organizationWorkspace ? (
        institutionalWorkspace
          ? <InstitutionPage api={api} reloadAccess={reloadAccess} />
          : <OrganizationSetupPage api={api} onActivated={reloadAccess} />
      ) : path === "/trabajos" && supportEnabled ? (
        <JobsAdminPage api={api} />
      ) : path === "/doctrina" && supportEnabled ? (
        <DoctrineAdminPage api={api} />
      ) : path === "/soporte" && supportEnabled ? (
        <SupportImpersonationPage api={api} access={supportAccess} />
      ) : path === "/firma" ? (
        <SignatureSettingsPage
          loadProfile={api.profile}
          saveSignature={api.updateLegalSignature}
        />
      ) : path === "/whatsapp" ? (
        <WhatsappSettingsPage api={api} />
      ) : (
        <CreditsSettingsPage
          loadCredits={api.credits}
          loadActivity={api.creditActivity}
          loadBilling={api.billing}
          createCreditOrder={api.createCreditOrder}
          createSubscription={api.createSubscription}
          reconcileBillingReturn={api.reconcileBillingReturn}
          cancelSubscription={api.cancelSubscription}
          changeSubscription={api.changeSubscription}
        />
      )}
    </ProductShell>
  );
}

function ClerkApp() {
  const getToken = useAccessToken();
  const accountAvatar = useSuiteAccountAvatar();
  const { freshSignIn, preferences } = useSuitePreferences();
  const api = useMemo(() => createApi(getToken), [getToken]);
  const [supportAccess, setSupportAccess] = useState(null);
  const [invitationAccess, setInvitationAccess] = useState(null);
  const [creditAdminAccess, setCreditAdminAccess] = useState(null);
  const [billingAdminAccess, setBillingAdminAccess] = useState(null);
  const [activityAdminAccess, setActivityAdminAccess] = useState(null);
  const accessState = useTribunaleaAccess({ apiUrl });
  const handledSignInRedirect = useRef(false);

  useEffect(() => {
    if (
      handledSignInRedirect.current
      || !freshSignIn
      || accessState.loading
      || accessState.error
      || !accessState.access
    ) return;

    handledSignInRedirect.current = true;
    if (window.location.pathname === "/invitacion") return;
    const rememberedProduct = products.find((product) => (
      product.applicationId === preferences.lastProduct
    ));
    if (
      rememberedProduct
      && canAccessApplication(
        accessState.access,
        rememberedProduct.applicationId,
      )
    ) {
      window.location.replace(rememberedProduct.href);
    }
  }, [
    accessState.access,
    accessState.error,
    accessState.loading,
    freshSignIn,
    preferences.lastProduct,
  ]);

  useEffect(() => {
    let active = true;
    Promise.allSettled([
      api.impersonationStatus(),
      api.invitationAccess(),
      api.creditGrantAccess(),
      api.billingAdminAccess(),
      api.activityAdminAccess(),
    ]).then(([
      supportResult,
      invitationResult,
      creditAdminResult,
      billingResult,
      activityResult,
    ]) => {
      if (!active) return;
      setSupportAccess(
        supportResult.status === "fulfilled"
          ? supportResult.value
          : { enabled: false },
      );
      setInvitationAccess(
        invitationResult.status === "fulfilled"
          ? invitationResult.value
          : { enabled: false },
      );
      setCreditAdminAccess(
        creditAdminResult.status === "fulfilled"
          ? creditAdminResult.value
          : { enabled: false },
      );
      setBillingAdminAccess(
        billingResult.status === "fulfilled"
          ? billingResult.value
          : { enabled: false },
      );
      setActivityAdminAccess(
        activityResult.status === "fulfilled"
          ? activityResult.value
          : { enabled: false },
      );
    });
    return () => {
      active = false;
    };
  }, [api]);

  return (
    <ApiContext.Provider value={api}>
      <AccountRoutes
        access={accessState.access}
        accessError={accessState.error}
        accessLoading={accessState.loading}
        accountAvatar={accountAvatar}
        authEnabled
        accountControls={<SuiteAccountMenuControls />}
        getAccessToken={getToken}
        supportAccess={supportAccess}
        invitationAccess={invitationAccess}
        creditAdminAccess={creditAdminAccess}
        billingAdminAccess={billingAdminAccess}
        activityAdminAccess={activityAdminAccess}
        reloadAccess={accessState.reload}
      />
    </ApiContext.Provider>
  );
}

function SignInScreen() {
  return (
    <AuthPortal
      productName="ECOSISTEMA JURÍDICO"
      headline="Tecnología para un sistema jurídico más inteligente."
      description="Tribunalea reúne herramientas digitales potenciadas por inteligencia artificial para simplificar el trabajo, el acceso a la información y la interacción con el sistema jurídico argentino."
      tools={[
        {
          name: "Praxis",
          audience: "Profesionales",
          description: "Gestioná causas, movimientos y tareas de tu práctica jurídica.",
        },
        {
          name: "Instrumenta",
          audience: "Equipos",
          description: "Convertí documentos e información dispersa en respuestas verificables.",
        },
        {
          name: "Civita",
          audience: "Ciudadanos",
          description: "Comprendé documentos, organizá problemas y prepará próximos pasos.",
        },
        {
          name: "Lexia",
          audience: "Estudiantes",
          description: "Estudiá normas, fallos y doctrina entendiendo sus fuentes.",
        },
      ]}
      proofPoints={[
        "Una misma identidad",
        "Herramientas especializadas",
        "El criterio siempre es humano",
      ]}
    />
  );
}

export default function App({ authEnabled }) {
  if (authEnabled) {
    const invitationPath = window.location.pathname === "/invitacion";
    return (
      <AuthenticatedArea signedOut={
        invitationPath
          ? <InviteAcceptancePortal />
          : <SignInScreen />
      }>
        <ClerkApp />
      </AuthenticatedArea>
    );
  }
  const api = createApi(null);
  return (
    <ApiContext.Provider value={api}>
      <AccountRoutes
        access={createFullSuiteAccess()}
        authEnabled={false}
        getAccessToken={null}
        supportAccess={{ enabled: false }}
        invitationAccess={{ enabled: false }}
        creditAdminAccess={{ enabled: false }}
        billingAdminAccess={{ enabled: false }}
        activityAdminAccess={{ enabled: false }}
      />
    </ApiContext.Provider>
  );
}
